import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

type PlanForm = {
  id?: number;
  name: string;
  slug: string;
  priceMinor: number;
  currency: string;
  interval: 'month' | 'year';
  trialDays: number;
  features: string[];
  isActive: boolean;
  sortOrder: number;
};

const blank = (): PlanForm => ({
  name: '',
  slug: '',
  priceMinor: 0,
  currency: 'INR',
  interval: 'month',
  trialDays: 0,
  features: [],
  isActive: true,
  sortOrder: 0,
});

function PlansAdmin() {
  const utils = trpc.useUtils();
  const list = trpc.billing.plans.list.useQuery({ activeOnly: false });

  const [opened, { open, close }] = useDisclosure(false);
  const [form, setForm] = useState<PlanForm>(blank());

  const upsert = trpc.billing.plans.upsert.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Plan saved', color: 'green' });
      void utils.billing.plans.list.invalidate();
      close();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const deactivate = trpc.billing.plans.deactivate.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Plan deactivated', color: 'green' });
      void utils.billing.plans.list.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // Explicit shape — `NonNullable<typeof list.data>[number]` blew tsc's
  // recursion limit (TS2589) at the tRPC + Mantine intersection.
  interface PlanRow {
    id: number;
    name: string;
    slug: string;
    priceMinor: number;
    currency: string;
    interval: string;
    trialDays: number;
    features: unknown;
    isActive: boolean;
    sortOrder: number;
  }
  const editPlan = (p: PlanRow) => {
    setForm({
      id: p.id,
      name: p.name,
      slug: p.slug,
      priceMinor: p.priceMinor,
      currency: p.currency,
      interval: p.interval as 'month' | 'year',
      trialDays: p.trialDays,
      features: (p.features as string[]) ?? [],
      isActive: p.isActive,
      sortOrder: p.sortOrder,
    });
    open();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Subscription plans</Title>
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={() => {
              setForm(blank());
              open();
            }}
          >
            New plan
          </Button>
        </Group>

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={300} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Slug</Table.Th>
                  <Table.Th>Price</Table.Th>
                  <Table.Th>Interval</Table.Th>
                  <Table.Th>Trial</Table.Th>
                  <Table.Th>Active</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {((list.data ?? []) as readonly PlanRow[]).map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td>{p.name}</Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace" c="dimmed">
                        {p.slug}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {p.priceMinor === 0
                        ? 'Free'
                        : `${p.currency} ${(p.priceMinor / 100).toLocaleString()}`}
                    </Table.Td>
                    <Table.Td>{p.interval}</Table.Td>
                    <Table.Td>{p.trialDays}d</Table.Td>
                    <Table.Td>
                      <Badge color={p.isActive ? 'green' : 'gray'} variant="light">
                        {p.isActive ? 'active' : 'inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap="xs" justify="flex-end">
                        <ActionIcon variant="subtle" aria-label="Edit" onClick={() => editPlan(p)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="Deactivate"
                          disabled={!p.isActive}
                          onClick={() => deactivate.mutate({ id: p.id })}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={opened}
        onClose={close}
        title={form.id ? `Edit plan: ${form.name}` : 'New plan'}
        size="lg"
        centered
      >
        <Stack>
          <Group grow>
            <TextInput
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            />
            <TextInput
              label="Slug"
              required
              description="lowercase letters, digits, hyphens"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.currentTarget.value })}
            />
          </Group>
          <Group grow>
            <NumberInput
              label="Price (minor units)"
              description="e.g. paise — 99900 = ₹999"
              min={0}
              value={form.priceMinor}
              onChange={(v) => setForm({ ...form, priceMinor: Number(v) || 0 })}
            />
            <TextInput
              label="Currency"
              maxLength={3}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.currentTarget.value.toUpperCase() })}
            />
            <Select
              label="Interval"
              data={[
                { value: 'month', label: 'Monthly' },
                { value: 'year', label: 'Yearly' },
              ]}
              value={form.interval}
              onChange={(v) => setForm({ ...form, interval: (v as 'month' | 'year') ?? 'month' })}
            />
          </Group>
          <Group grow>
            <NumberInput
              label="Trial days"
              min={0}
              max={365}
              value={form.trialDays}
              onChange={(v) => setForm({ ...form, trialDays: Number(v) || 0 })}
            />
            <NumberInput
              label="Sort order"
              value={form.sortOrder}
              onChange={(v) => setForm({ ...form, sortOrder: Number(v) || 0 })}
            />
          </Group>
          <TagsInput
            label="Feature bullets"
            placeholder="Press Enter to add"
            value={form.features}
            onChange={(v) => setForm({ ...form, features: v })}
          />
          <Switch
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />
          <Group justify="end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button loading={upsert.isPending} onClick={() => upsert.mutate(form)}>
              Save plan
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/subscription-plans')({
  component: PlansAdmin,
});
